
import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, TrendingUp, TrendingDown, Activity, Users, MapPin, BarChart3, AlertTriangle, FileText } from 'lucide-react';
import { useTrendsData } from '@/hooks/useTrendsData';
import { useLiteMode } from '@/hooks/useLiteMode';
import { TrendsFilters } from '@/components/TrendsFilters';
import { TrendsHeader } from '@/components/trends/TrendsHeader';
import { FilterResultsSummary } from '@/components/trends/FilterResultsSummary';
import { TrendingTopicsList } from '@/components/trends/TrendingTopicsList';
import { ZoneSentiment } from '@/components/trends/ZoneSentiment';
import { useUserFilters } from '@/hooks/useUserFilters';
import { useUserRole } from '@/hooks/useUserRole';

export const Trends = () => {
  const { userProfile, userCrops, loading: userLoading } = useUserFilters();
  const { trendingTopics, zoneSentiments, communities, loading, error, refreshData, manualRefresh } = useTrendsData();
  const { isAdmin } = useUserRole();
  const { isLiteMode } = useLiteMode();
  const [selectedState, setSelectedState] = useState("All States");
  const [selectedCrop, setSelectedCrop] = useState("All Crops");
  const [selectedCommunity, setSelectedCommunity] = useState("All Communities");
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  // In lite mode, limit the data shown
  const maxTrendingTopics = isLiteMode ? 5 : 10;
  const maxZoneSentiments = isLiteMode ? 3 : 6;

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    await manualRefresh();
    setIsManualRefreshing(false);
  };

  const handleRefreshClick = () => {
    refreshData();
  };

  // Filter function for topics
  const filterTopics = (topics: any[], stateFilter: string, cropFilter: string, communityFilter: string) => {
    return topics.filter(topic => {
      const stateMatch = stateFilter === "All States" || 
        (topic.plantHardinessZone && topic.plantHardinessZone.includes(stateFilter)) ||
        (userProfile?.location && userProfile.location.includes(stateFilter));
      
      const cropMatch = cropFilter === "All Crops" || 
        (topic.topic && topic.topic.toLowerCase().includes(cropFilter.toLowerCase()));
      
      const communityMatch = communityFilter === "All Communities" || 
        (topic.topic && topic.topic.toLowerCase().includes(communityFilter.toLowerCase().replace(/\s+/g, ' ')));
      
      return stateMatch && cropMatch && communityMatch;
    });
  };

  const filteredTopics = filterTopics(trendingTopics, selectedState, selectedCrop, selectedCommunity);
  const challengeTopics = filteredTopics.filter(t => t.category === 'Challenges');

  if (loading || userLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Social Listening Analytics</h1>
          <p className="text-white/90">Loading community insights and trends...</p>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-white/10 rounded-lg"></div>
          <div className="h-32 bg-white/10 rounded-lg"></div>
          <div className="h-32 bg-white/10 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Social Listening Analytics</h1>
          <p className="text-white/90 mb-4">Unable to load social listening data</p>
          <Button 
            onClick={handleRefreshClick}
            variant="outline"
            className="text-white border-white hover:bg-white hover:text-green-600"
          >
            <RefreshCw size={16} className="mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <TrendsHeader
        loading={loading}
        trendingTopicsCount={trendingTopics.length}
        communitiesCount={communities.length}
        isManualRefreshing={isManualRefreshing}
        onRefresh={handleRefreshClick}
        onManualRefresh={handleManualRefresh}
        isAdmin={isAdmin}
      />

      <TrendsFilters
        userProfile={userProfile}
        userCrops={userCrops}
        communities={communities}
        selectedState={selectedState}
        selectedCrop={selectedCrop}
        selectedCommunity={selectedCommunity}
        onStateChange={setSelectedState}
        onCropChange={setSelectedCrop}
        onCommunityChange={setSelectedCommunity}
      />

      <FilterResultsSummary
        selectedState={selectedState}
        selectedCrop={selectedCrop}
        selectedCommunity={selectedCommunity}
        filteredTopicsCount={filteredTopics.length}
      />

      {/* Summary Cards for Social Listening */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-white/95 border-white/80 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Trending Challenges</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{challengeTopics.length}</div>
            <p className="text-xs text-slate-500">
              Challenges discussed by farmers
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white/95 border-white/80 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Geographic Insights</CardTitle>
            <MapPin className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{zoneSentiments.length}</div>
            <p className="text-xs text-slate-500">
              Regions with sentiment data
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white/95 border-white/80 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Active Communities</CardTitle>
            <Users className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{communities.length}</div>
            <p className="text-xs text-slate-500">
              Engaged community groups
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Social Listening Analytics */}
      <Card className="bg-white/95 border-white/80 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2 text-slate-800 text-lg font-semibold">
            <BarChart3 className="w-5 h-5 text-slate-600" />
            Social Listening Analytics
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleManualRefresh}
            disabled={isManualRefreshing}
            className="text-slate-600 border-slate-300 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isManualRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {filteredTopics.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-600 mb-4">No trending topics found</p>
              <p className="text-sm text-slate-500">
                Create posts with challenges and sentiments to see analytics
              </p>
            </div>
          ) : (
            <TrendingTopicsList 
              trends={filteredTopics.slice(0, maxTrendingTopics)}
              showingCount={maxTrendingTopics}
              onShowMore={() => {}}
              isLiteMode={isLiteMode}
            />
          )}
        </CardContent>
      </Card>

      {/* Community Engagement */}
      <Card className="bg-white/95 border-white/80 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-slate-800 text-lg font-semibold">
            <Users className="w-5 h-5 text-slate-600" />
            Community Engagement Trends
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {(() => {
            // Filter communities to only show those with activity
            const activeCommunities = communities.filter(community => 
              (community.members_count && community.members_count > 0) ||
              (community.post_count && community.post_count > 0) ||
              (community.engagement_score && community.engagement_score > 0)
            );

            return activeCommunities.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-600">No active community engagement data available yet</p>
                <p className="text-sm text-slate-500 mt-2">
                  Add profile tags and create posts to see community trends
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeCommunities.map((community) => (
                <div 
                  key={community.id} 
                  className="group flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all duration-200"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-slate-800 text-sm truncate">
                        {community.name}
                      </h4>
                      {community.growth_percentage && community.growth_percentage !== 0 && (
                        <Badge 
                          variant={community.growth_percentage > 0 ? "default" : "secondary"}
                          className="text-xs px-2 py-0.5 h-auto bg-green-100 text-green-700 hover:bg-green-100"
                        >
                          {community.growth_percentage > 0 ? (
                            <TrendingUp className="w-3 h-3 mr-1" />
                          ) : (
                            <TrendingDown className="w-3 h-3 mr-1" />
                          )}
                          {Math.abs(community.growth_percentage).toFixed(0)}%
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {(community.members_count || 0).toLocaleString()}
                      </span>
                      {community.post_count && (
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {community.post_count}
                        </span>
                      )}
                      {community.engagement_score && (
                        <span className="flex items-center gap-1">
                          <Activity className="w-3 h-3" />
                          {community.engagement_score.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-slate-800">
                      {community.members_count || 0}
                    </div>
                  </div>
                </div>
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
};
